import { createSlice } from '@reduxjs/toolkit';
import {
  fetchSongLibraries,
  fetchSongLibrary,
  addSongLibrary,
  editSongLibrary,
  removeSongLibrary,
  importSongLibraryFromCsv
} from "./thunk";

const SongLibrarySlice = createSlice({
  name: 'SongLibrary',
  initialState: {
    items: [],
    currentLibrary: null,
    loading: false,
    importing: false,
    error: null,
    success: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all libraries
      .addCase(fetchSongLibraries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSongLibraries.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchSongLibraries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch single library
      .addCase(fetchSongLibrary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSongLibrary.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLibrary = action.payload;
      })
      .addCase(fetchSongLibrary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create library
      .addCase(addSongLibrary.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(addSongLibrary.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
        state.success = 'Library created successfully';
      })
      .addCase(addSongLibrary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update library
      .addCase(editSongLibrary.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(editSongLibrary.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        state.success = 'Library updated successfully';
      })
      .addCase(editSongLibrary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete library
      .addCase(removeSongLibrary.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(removeSongLibrary.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(item => item.id !== action.payload);
        state.success = 'Library deleted successfully';
      })
      .addCase(removeSongLibrary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Import songs from CSV
      .addCase(importSongLibraryFromCsv.pending, (state) => {
        state.importing = true;
        state.error = null;
        state.success = null;
      })
      .addCase(importSongLibraryFromCsv.fulfilled, (state, action) => {
        state.importing = false;
        const index = state.items.findIndex(item => item.id === action.payload.libraryId);
        if (index !== -1) {
          state.items[index] = {
            ...state.items[index],
            songCount: (state.items[index].songCount || 0) + action.payload.added,
          };
        }
      })
      .addCase(importSongLibraryFromCsv.rejected, (state, action) => {
        state.importing = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccess } = SongLibrarySlice.actions;

// Selectors
export const selectAllLibraries = (state) => state.SongLibrary.items;
export const selectCurrentLibrary = (state) => state.SongLibrary.currentLibrary;
export const selectLibrariesLoading = (state) => state.SongLibrary.loading;
export const selectLibraryImporting = (state) => state.SongLibrary.importing;
export const selectLibrariesError = (state) => state.SongLibrary.error;
export const selectLibrariesSuccess = (state) => state.SongLibrary.success;

export default SongLibrarySlice.reducer;
